"use client"

import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import type { IntentDescription } from "@src/components/DefuseSDK/features/machines/swapIntentMachine"
import { invalidateBalanceQueries } from "@src/components/DefuseSDK/queries/balanceQueries"
import { formatTokenValue } from "@src/components/DefuseSDK/utils/format"
import { SwapStatus } from "@src/components/SwapStatus"
import { journeyAnalytics } from "@src/features/analytics/journeyAnalytics"
import {
  classifyEarnVaultOperation,
  formatEarnVaultActivityTitle,
} from "@src/features/earn/lib/earnVaultToken"
import {
  type RegisterSwapParams,
  swapTrackerMachine,
  type TrackedSwapRef,
} from "@src/machines/swapTrackerMachine"
import { useSelector } from "@xstate/react"
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { createActor } from "xstate"
import { useActivityDock } from "./ActivityDockProvider"

export type TrackedSwapIntent = TrackedSwapRef

// Generic, campaign-agnostic settlement fact. Consumers decide what it means;
// the tracker holds no campaign identity, storage, or query policy.
export type SwapSettlement = {
  swapId: string
  accountId: string | null
  isConfidential: boolean
  succeeded: boolean
  completedAt: Date | null
}

type SwapSettlementListener = (settlement: SwapSettlement) => void

// A one-click swap counts as settled successfully only on an explicit SUCCESS;
// FAILED/REFUNDED and other terminal statuses are treated as not succeeded.
export function isOneClickSuccess(status: string): boolean {
  return status === "SUCCESS"
}

type SwapTrackerMachineContextType = {
  trackedSwaps: TrackedSwapIntent[]
  registerSwap: (params: RegisterSwapParams) => void
  dismissSwap: (id: string) => void
  markSwapSeen: (id: string) => void
  hasActiveSwap: (id: string) => boolean
  subscribeToSettlements: (listener: SwapSettlementListener) => () => void
}

const SwapTrackerMachineContext = createContext<
  SwapTrackerMachineContextType | undefined
>(undefined)

export function SwapTrackerMachineProvider({
  children,
}: {
  children: ReactNode
}) {
  const [actorRef] = useState(() => createActor(swapTrackerMachine).start())
  const { addDockItem, removeDockItem, settleDockItem } = useActivityDock()
  const settlementListenersRef = useRef(new Set<SwapSettlementListener>())

  const trackedSwaps = useSelector(actorRef, (s) => s.context.swapRefs)

  const subscribeToSettlements = useCallback(
    (listener: SwapSettlementListener) => {
      settlementListenersRef.current.add(listener)
      return () => {
        settlementListenersRef.current.delete(listener)
      }
    },
    []
  )

  useEffect(() => {
    const emitSettlement = (swapId: string, succeeded: boolean) => {
      const swap = actorRef
        .getSnapshot()
        .context.swapRefs.find((s) => s.id === swapId)
      const settlement: SwapSettlement = {
        swapId,
        accountId: swap?.accountId ?? null,
        isConfidential: swap?.isConfidential === true,
        succeeded,
        completedAt: swap?.completedAt ?? null,
      }
      if (succeeded && swap?.swapJourney != null) {
        journeyAnalytics.completed({
          ...swap.swapJourney,
          operation: { namespace: "intent", key: swap.intentHash },
        })
      }
      for (const listener of settlementListenersRef.current) {
        listener(settlement)
      }
    }

    const sub1 = actorRef.on("INTENT_SETTLED", (event) => {
      settleDockItem(`swap-${event.data.intentHash}`)
      invalidateBalanceQueries()
      emitSettlement(event.data.intentHash, true)
    })
    const sub2 = actorRef.on("ONE_CLICK_SETTLED", (event) => {
      settleDockItem(`swap-${event.data.depositAddress}`)
      invalidateBalanceQueries()
      emitSettlement(
        event.data.depositAddress,
        isOneClickSuccess(event.data.status)
      )
    })
    return () => {
      sub1.unsubscribe()
      sub2.unsubscribe()
    }
  }, [actorRef, settleDockItem])

  const registerSwap = useCallback(
    (params: RegisterSwapParams) => {
      if (params.intentDescription.type !== "swap") return

      const id = params.depositAddress ?? params.intentHash
      const swapDescription = params.intentDescription as Extract<
        IntentDescription,
        { type: "swap" }
      >

      actorRef.send({ type: "REGISTER_SWAP", params })

      const swap = actorRef
        .getSnapshot()
        .context.swapRefs.find((s) => s.id === id)
      if (!swap) return

      const formattedIn = formatTokenValue(
        swapDescription.totalAmountIn.amount,
        swapDescription.totalAmountIn.decimals,
        { min: 0.0001, fractionDigits: 4 }
      )
      const formattedOut = formatTokenValue(
        swapDescription.totalAmountOut.amount,
        swapDescription.totalAmountOut.decimals,
        { min: 0.0001, fractionDigits: 4 }
      )

      const earnOperation = classifyEarnVaultOperation(
        params.tokenIn,
        params.tokenOut
      )
      const earnVaultToken =
        earnOperation === "deposit"
          ? params.tokenOut
          : earnOperation === "withdraw"
            ? params.tokenIn
            : null
      const earnPaymentAmountFormatted =
        earnOperation === "deposit"
          ? formattedIn
          : earnOperation === "withdraw"
            ? formattedOut
            : null
      const earnPaymentToken =
        earnOperation === "deposit"
          ? params.tokenIn
          : earnOperation === "withdraw"
            ? params.tokenOut
            : null

      addDockItem({
        id: `swap-${id}`,
        title:
          earnOperation != null &&
          earnVaultToken != null &&
          earnPaymentAmountFormatted != null &&
          earnPaymentToken != null
            ? formatEarnVaultActivityTitle(
                earnOperation,
                earnVaultToken,
                earnPaymentAmountFormatted,
                earnPaymentToken.symbol
              )
            : `${formattedIn} ${params.tokenIn.symbol} → ${formattedOut} ${params.tokenOut.symbol}`,
        icons:
          earnPaymentToken != null
            ? [
                <AssetComboIcon
                  key="earn"
                  sizeClassName="size-7"
                  {...earnPaymentToken}
                />,
              ]
            : [
                <AssetComboIcon
                  key="in"
                  sizeClassName="size-7"
                  {...params.tokenIn}
                />,
                <AssetComboIcon
                  key="out"
                  sizeClassName="size-7"
                  {...params.tokenOut}
                />,
              ],
        keyValueRows: [],
        renderContent: () => <SwapStatus variant="dock" swap={swap} />,
      })
    },
    [actorRef, addDockItem]
  )

  const dismissSwap = useCallback(
    (id: string) => {
      actorRef.send({ type: "DISMISS_SWAP", id })
      removeDockItem(`swap-${id}`)
    },
    [actorRef, removeDockItem]
  )

  const markSwapSeen = useCallback(
    (id: string) => {
      actorRef.send({ type: "MARK_SWAP_SEEN", id })
    },
    [actorRef]
  )

  const hasActiveSwap = useCallback(
    (id: string) => trackedSwaps.some((s) => s.id === id),
    [trackedSwaps]
  )

  const value = useMemo(
    () => ({
      trackedSwaps,
      registerSwap,
      dismissSwap,
      markSwapSeen,
      hasActiveSwap,
      subscribeToSettlements,
    }),
    [
      trackedSwaps,
      registerSwap,
      dismissSwap,
      markSwapSeen,
      hasActiveSwap,
      subscribeToSettlements,
    ]
  )

  return (
    <SwapTrackerMachineContext.Provider value={value}>
      {children}
    </SwapTrackerMachineContext.Provider>
  )
}

export function useSwapTrackerMachine() {
  const context = useContext(SwapTrackerMachineContext)

  if (!context) {
    throw new Error(
      "useSwapTrackerMachine must be used within a SwapTrackerMachineProvider"
    )
  }

  return context
}
